import { INVALID_CURRENT_PASSWORD_CODE, SAME_PASSWORD_CODE, changeOwnPassword } from "@/api/auth";
import { ApiError } from "@/api/errors";
import { currentOwnerKey } from "@/api/session";
import { useAuth } from "@/app/auth";
import { useOnline } from "@/app/online";
import { forgetRememberedSecret, rememberSecret, rememberedSecret } from "@/app/relogin";
import { useSession } from "@/app/session";
import { armPinOffer, wipeVault } from "@/app/vault";
import { useMutation } from "@tanstack/react-query";
import { KeyRound, LoaderCircle, WifiOff } from "lucide-react";
import { type FormEvent, useState } from "react";

const MIN_PASSWORD_LENGTH = 10;

const OFFLINE_MESSAGE =
  "Sin señal: cambiar la contraseña necesita conexión porque la valida el servidor. Buscá señal y volvé a intentar.";

function messageOf(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.code === INVALID_CURRENT_PASSWORD_CODE) {
      return "La contraseña actual no es esa. Probá de nuevo.";
    }
    if (cause.code === SAME_PASSWORD_CODE) {
      return "La nueva tiene que ser distinta de la que estás usando.";
    }
    if (cause.status === undefined && !navigator.onLine) {
      return OFFLINE_MESSAGE;
    }
    return cause.message;
  }
  if (!navigator.onLine) {
    return OFFLINE_MESSAGE;
  }
  return "No pudimos hablar con el servidor. Probá de nuevo en unos segundos.";
}

export function ChangePasswordScreen() {
  const session = useSession();
  const { signIn, signOutFromDevice } = useAuth();
  const online = useOnline();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);

  const identifier =
    rememberedSecret()?.identifier ?? session.user.email ?? session.user.phone ?? "";
  const tenantId = rememberedSecret()?.tenantId ?? null;

  const change = useMutation({
    mutationFn: () => changeOwnPassword(current, next),
    onSuccess: async (token: string) => {
      await wipeVault(currentOwnerKey());
      if (identifier === "") {
        forgetRememberedSecret();
      } else {
        rememberSecret({ kind: "password", identifier, password: next, tenantId });
        armPinOffer();
      }
      signIn(token);
    },
    onError: (cause: unknown) => setError(messageOf(cause)),
  });

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = repeat.length > 0 && repeat !== next;
  const canSubmit =
    online &&
    !change.isPending &&
    current !== "" &&
    next.length >= MIN_PASSWORD_LENGTH &&
    repeat === next;

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (canSubmit) {
      change.mutate();
    }
  }

  return (
    <main className="login">
      <div className="login__card">
        <KeyRound size={28} aria-hidden="true" />
        <h1 className="t-h1">Cambiá tu contraseña</h1>
        <p className="t-body">
          Antes de entrar tenés que elegir una contraseña propia. La de ahora te la dio el
          concesionario.
        </p>

        {online ? null : (
          <p className="notice notice--offline">
            <WifiOff size={16} aria-hidden="true" />
            {OFFLINE_MESSAGE}
          </p>
        )}

        <form className="login__form" onSubmit={onSubmit} noValidate>
          <label className="field" htmlFor="current-password">
            <span className="field__label">Contraseña actual</span>
            <input
              id="current-password"
              className="field__input"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </label>

          <label className="field" htmlFor="new-password">
            <span className="field__label">Contraseña nueva</span>
            <input
              id="new-password"
              className="field__input"
              type="password"
              autoComplete="new-password"
              aria-describedby="new-password-hint"
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
          </label>
          <p className="t-small" id="new-password-hint">
            Al menos {MIN_PASSWORD_LENGTH} caracteres.
          </p>

          <label className="field" htmlFor="repeat-password">
            <span className="field__label">Repetí la nueva</span>
            <input
              id="repeat-password"
              className="field__input"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
            />
          </label>

          {tooShort ? (
            <p className="notice notice--info">
              Te faltan caracteres: la nueva necesita {MIN_PASSWORD_LENGTH} o más.
            </p>
          ) : null}

          {mismatch ? <p className="notice notice--info">Las dos nuevas no coinciden.</p> : null}

          {error ? (
            <p className="notice notice--error" role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="btn btn--primary btn--block" disabled={!canSubmit}>
            {change.isPending ? <LoaderCircle size={18} aria-hidden="true" /> : null}
            {change.isPending ? "Cambiando…" : "Cambiar y entrar"}
          </button>
        </form>

        <button
          type="button"
          className="btn btn--block"
          onClick={() => void signOutFromDevice()}
          disabled={change.isPending}
        >
          Cerrar sesión
        </button>
      </div>
    </main>
  );
}
